export interface WorkspacePreferenceEntry<T = unknown> {
  id?: number | string
  key: string
  value: T
  updatedAt?: string
}

const MALFORMED_RESPONSE = 'Malformed workspace preference response'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function assertSuccessfulEnvelope(value: Record<string, unknown>): void {
  if ('success' in value && value.success !== true) {
    throw new TypeError(MALFORMED_RESPONSE)
  }
}

function rowsFrom(value: unknown, fallbackKey?: string): unknown[] {
  if (Array.isArray(value)) return value
  if (!isRecord(value)) throw new TypeError(MALFORMED_RESPONSE)
  assertSuccessfulEnvelope(value)

  const payload = 'data' in value ? value.data : value
  if (Array.isArray(payload)) return payload
  if (!isRecord(payload)) throw new TypeError(MALFORMED_RESPONSE)
  if (Array.isArray(payload.data)) return payload.data
  if (Object.keys(payload).length === 0) return []
  if ('key' in payload || ('value' in payload && fallbackKey)) return [payload]
  throw new TypeError(MALFORMED_RESPONSE)
}

function parseRow(value: unknown, fallbackKey?: string): WorkspacePreferenceEntry {
  if (!isRecord(value) || !('value' in value)) {
    throw new TypeError('Malformed workspace preference row')
  }
  const key = typeof value.key === 'string' && value.key.trim() ? value.key : fallbackKey
  if (!key) throw new TypeError('Malformed workspace preference key')

  const id = typeof value.id === 'string' || typeof value.id === 'number' ? value.id : undefined
  const updatedAt =
    typeof value.updated_at === 'string'
      ? value.updated_at
      : typeof value.updatedAt === 'string'
        ? value.updatedAt
        : undefined

  return {
    ...(id !== undefined ? { id } : {}),
    key,
    value: value.value,
    ...(updatedAt !== undefined ? { updatedAt } : {}),
  }
}

/** Decode list, bootstrap and legacy-array preference envelopes into one generic row shape. */
export function parseWorkspacePreferenceList(value: unknown): WorkspacePreferenceEntry[] {
  return rowsFrom(value).map((row) => parseRow(row))
}

/** Decode a keyed read. A successful missing-key envelope resolves to `null`. */
export function parseWorkspacePreference(
  value: unknown,
  key: string,
): WorkspacePreferenceEntry | null {
  const rows = rowsFrom(value, key).map((row) => parseRow(row, key))
  return rows.find((row) => row.key === key) ?? null
}

/** Writes are acknowledged explicitly; no request body is treated as confirmed without this. */
export function parseWorkspacePreferenceWrite(value: unknown): void {
  if (!isRecord(value) || value.success !== true) {
    throw new TypeError('Malformed workspace preference write response')
  }
}
